import { Input, Checkbox, GoogleInput, Listbox } from "@/components/ui/form";
import { Card } from "@/components/ui/card";
import { Callout } from "@/components/ui/callout";
import { Button } from "@/components/ui/button";
import { FormProvider } from "react-hook-form";
import { CheckIcon } from "@heroicons/react/16/solid";

export const PracticeForm = ({
    methods,
    onSubmit,
    isAddressSameAsPersonal,
    isGroupPractice,
    mutation,
}) => {
    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="grid grid-cols-12 gap-4">
                    <Card size="lg" className="grid-cols-12">
                        <div className="col-span-full grid grid-cols-subgrid lg:col-span-2 lg:mr-8">
                            <div className="col-span-full">
                                <h2 className="mb-3 text-xl font-semibold">
                                    Basic details
                                </h2>
                            </div>
                        </div>
                        <div className="col-span-full mb-6 grid max-w-6xl grid-cols-subgrid gap-4 lg:col-span-6">
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Input
                                    name="practiceTitle"
                                    label="Practice/Trading name"
                                    placeholder="Practice/Trading name"
                                />
                            </div>
                        </div>
                    </Card>
                    <Card size="lg" className="grid-cols-12">
                        <div className="col-span-full grid grid-cols-subgrid lg:col-span-2 lg:mr-8">
                            <div className="col-span-full">
                                <h2 className="mb-3 text-xl font-semibold">
                                    Practice/Service address
                                </h2>
                            </div>
                        </div>
                        <div className="col-span-full mb-6 grid max-w-6xl grid-cols-subgrid gap-4 lg:col-span-6">
                            <div className="col-span-full mt-2">
                                <Checkbox
                                    name="isAddressSameAsPersonal"
                                    label="Same as personal address"
                                    labelPlacement="end"
                                    className="w-fit"
                                />
                            </div>
                            {!isAddressSameAsPersonal && (
                                <div className="col-span-full mb-6 grid max-w-6xl grid-cols-subgrid gap-4 lg:col-span-6">
                                    <div className="col-span-full grid grid-cols-subgrid items-end">
                                        <div className="col-span-full md:col-span-6 lg:col-span-3">
                                            <GoogleInput
                                                name="findAddress"
                                                label="Find Address"
                                                placeholder="Find Address"
                                                setValue={methods.setValue}
                                            />
                                        </div>
                                        <div className="col-span-full mt-2 md:col-span-6 md:mt-0 lg:col-span-3">
                                            <Callout
                                                variant="info"
                                                icon
                                                size="sm"
                                            >
                                                <p>
                                                    The address patients will
                                                    visit, shown on your profile
                                                    and used to match you with
                                                    nearby patients.
                                                </p>
                                            </Callout>
                                        </div>
                                    </div>
                                    <div className="col-span-full md:col-span-6 lg:col-span-3">
                                        <Input
                                            name="addressNumber"
                                            label="House number/name"
                                            placeholder="House number/name"
                                        />
                                    </div>
                                    <div className="col-span-full md:col-span-6 lg:col-span-3">
                                        <Input
                                            name="addressLine1"
                                            label="First line"
                                            placeholder="First line"
                                        />
                                    </div>
                                    <div className="col-span-full md:col-span-6 lg:col-span-3">
                                        <Input
                                            name="addressLine2"
                                            label="Second line"
                                            placeholder="Second line"
                                        />
                                    </div>
                                    <div className="col-span-full md:col-span-6 lg:col-span-3">
                                        <Input
                                            name="addressTown"
                                            label="Town"
                                            placeholder="Town"
                                        />
                                    </div>
                                    <div className="col-span-full md:col-span-6 lg:col-span-3">
                                        <Input
                                            name="addressCounty"
                                            label="County"
                                            placeholder="County"
                                            autoComplete="address-level1"
                                        />
                                    </div>
                                    <div className="col-span-full md:col-span-6 lg:col-span-3">
                                        <Input
                                            name="addressPostalCode"
                                            label="Postcode"
                                            placeholder="Postcode"
                                        />
                                    </div>
                                    <div className="hidden">
                                        <Input
                                            name="addressLatitude"
                                            type="hidden"
                                        />
                                        <Input
                                            name="addressLongitude"
                                            type="hidden"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                    <Card size="lg" className="grid-cols-12">
                        <div className="col-span-full grid grid-cols-subgrid lg:col-span-2 lg:mr-8">
                            <div className="col-span-full">
                                <h2 className="mb-3 text-xl font-semibold">
                                    How you work
                                </h2>
                            </div>
                        </div>
                        <div className="col-span-full mb-6 grid max-w-6xl grid-cols-subgrid items-end gap-4 lg:col-span-6">
                            <div className="col-span-full md:col-span-6 lg:col-span-3">
                                <Listbox
                                    name="workArrangement"
                                    label="How you work"
                                    placeholder="How you work"
                                >
                                    <option value={1}>In-person</option>
                                    <option value={2}>Remote</option>
                                    <option value={3}>Both</option>
                                </Listbox>
                            </div>
                            <div className="col-span-full mt-2 md:col-span-6 md:mt-0 lg:col-span-3">
                                <Callout variant="info" icon size="sm">
                                    <p>
                                        Let patients know whether you see them
                                        in person, remotely, or both.
                                    </p>
                                </Callout>
                            </div>
                            <div className="col-span-full">
                                <Checkbox
                                    name="isGroupPractice"
                                    label="Do you belong to a group practice?"
                                    labelPlacement="end"
                                    className="w-fit"
                                />
                            </div>
                            {isGroupPractice && (
                                <div className="col-span-full mt-4 grid grid-cols-subgrid gap-x-4 gap-y-4">
                                    <div className="col-span-full text-sm md:col-span-10 md:text-base">
                                        <h2 className="mb-3 text-xl font-semibold">
                                            Practice admin contact
                                        </h2>
                                    </div>
                                    <div className="col-span-full md:col-span-6 lg:col-span-3">
                                        <Input
                                            name="adminName"
                                            label="Name"
                                            placeholder="Name"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="col-span-full md:col-span-6 lg:col-span-3">
                                        <Input
                                            name="adminEmail"
                                            label="Email"
                                            placeholder="Email"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="col-span-full md:col-span-6 lg:col-span-3">
                                        <Input
                                            name="adminPhoneNumber"
                                            label="Telephone number"
                                            placeholder="Telephone number"
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                            )}
                            <div className="col-span-full mt-10 flex gap-3">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    className="w-fit"
                                    disabled={mutation.isPending}
                                >
                                    <CheckIcon className="stroke-2" />
                                    {mutation.isPending ? "Saving..." : "Save"}
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>
            </form>
        </FormProvider>
    );
};
